/**
 * CSRF and AJAX API helpers for booking module
 * Based on the old appointment.js logic
 */
import { qs } from './utils.js';
import { CFG } from './config.js';


/**
 * Get CSRF token from meta tag
 */
export function getCsrf() {
    const name = document.querySelector('meta[name="csrf_token_name"]')?.getAttribute('content') || 'csrf_token';
    const hash = document.querySelector('meta[name="csrf_token"]')?.getAttribute('content') || '';
    return { name, hash };
}

/**
 * Update CSRF token from XHR response headers
 */
export function setCsrfFromXhr(xhr) {
    const newHash = xhr.getResponseHeader('X-CSRF-TOKEN');
    const newName = xhr.getResponseHeader('X-CSRF-TOKEN-NAME');
    if (newHash) {
        const meta = document.querySelector('meta[name="csrf_token"]');
        if (meta) meta.setAttribute('content', newHash);
    }
    if (newName) {
        const meta = document.querySelector('meta[name="csrf_token_name"]');
        if (meta) meta.setAttribute('content', newName);
    }
}

/**
 * Generic AJAX request wrapper
 */
function request(url, options = {}) {
    const {
        method = 'POST',
        data = null,
        headers = {},
        onSuccess = null,
        onError = null,
        onComplete = null,
        beforeSend = null
    } = options;

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url, true);

        // Set default headers
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.setRequestHeader('Content-Type', 'application/json');

        const csrf = getCsrf();
        xhr.setRequestHeader('X-CSRF-TOKEN', csrf.hash);

        // Set custom headers
        Object.keys(headers).forEach(key => {
            xhr.setRequestHeader(key, headers[key]);
        });

        xhr.withCredentials = true;

        xhr.onload = function() {
            setCsrfFromXhr(xhr);
            
            if (xhr.status >= 200 && xhr.status < 300) {
                let response;
                try {
                    response = xhr.responseText ? JSON.parse(xhr.responseText) : {};
                } catch (e) {
                    response = xhr.responseText;
                }
                if (onSuccess) onSuccess(response, xhr);
                resolve(response);
            } else {
                const error = {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    responseText: xhr.responseText,
                    responseJSON: null
                };
                try {
                    error.responseJSON = JSON.parse(xhr.responseText);
                } catch (e) {
                    // ignore
                }
                if (onError) onError(xhr);
                reject(error);
            }
        };

        xhr.onerror = function() {
            const error = { status: 0, statusText: 'Network Error', responseText: '' };
            if (onError) onError(xhr);
            reject(error);
        };

        xhr.onloadend = function() {
            if (onComplete) onComplete(xhr);
        };

        if (beforeSend) beforeSend(xhr);

        // Send request
        if (data) {
            const csrf = getCsrf();
            const payload = typeof data === 'object' ? { ...data, [csrf.name]: csrf.hash } : data;
            xhr.send(JSON.stringify(payload));
        } else {
            xhr.send();
        }
    });
}

/**
 * Load prices dynamically from backend
 */
export function loadPrices({ location, patient_type, veteran, tz = 'UTC' } = {}) {
    return request('/appointment/prices', {
        method: 'POST',
        data: {
            location: location || '',
            patient_type: patient_type || '',
            veteran: veteran ? 1 : 0,
            tz: tz
        }
    });
}

/**
 * Save appointment options (service selection)
 */
export function saveAppointmentOptions({
    service_id,
    price,
    duration,
    location,
    practitioner,
    patient_type,
    veteran,
    veteran_id
} = {}) {
    return request('/appointment/options', {
        method: 'POST',
        data: {
            service_id,
            price,
            duration,
            location,
            practitioner,
            patient_type,
            veteran: veteran ? 1 : 0,
            veteran_id: veteran_id || ''
        }
    });
}


export function initClickTimeSlots(container, radios) {
    const stepCal = qs(container, '[data-calendar]');
    radios.forEach(r => {
        //console.log('Binding time slot radio', r);
        // Get timeslot value from parent element's data-timeslot attribute
        const label = r.closest('[data-timeslot]');
        const timeSlot = label?.getAttribute('data-timeslot') || label?.textContent?.trim() || '';
        
        //const label = stepCal.querySelector(`label[for="${r.id}"]`);
        const goNext = () => {
            // Get current date from calendar
            const calEl = qs(container, CFG.selectors.calendar);
            const dp = calEl?._airdp;
            const date = dp?.selectedDates?.[0] || null;
            const dateStr = date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString().slice(0, 10) : '';

              // if we do not have <meta name="csrf_token"> in the page, the request will fail, so we simulate  loading state for local testing 
                const csrfTokenMeta = document.querySelector('meta[name="csrf_token"]');
                if (!csrfTokenMeta) {
                    console.log('Simulating schedule save (no CSRF token found). Date:', dateStr, 'Time slot:', timeSlot);
                    const stepOption = qs(container, '[data-option]');
                    const stepCal = qs(container, '[data-calendar]');
                    const stepClient = qs(container, '[data-client-info]');
                    const stepPayment = qs(container, '[data-payment]');
                
                    const steps = [stepOption, stepCal, stepClient, stepPayment].filter(Boolean);
                    const hiddenClass = CFG.classes.hidden || 'd-none';
                
                    const showOnly = (el) => {
                        steps.forEach(s => s?.classList.add(hiddenClass));
                        el?.classList.remove(hiddenClass);
                        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    };

                    showOnly(stepClient);
                    
                    return;
                }

            if (dateStr && timeSlot) {
                saveSchedule({ date: dateStr, time: timeSlot })
                    .then(response => {
                        console.log('Schedule saved', response);

                        const stepOption = qs(container, '[data-option]');
                        const stepCal = qs(container, '[data-calendar]');
                        const stepClient = qs(container, '[data-client-info]');
                        const stepPayment = qs(container, '[data-payment]');
                    
                        const steps = [stepOption, stepCal, stepClient, stepPayment].filter(Boolean);
                        const hiddenClass = CFG.classes.hidden || 'd-none';
                    
                        const showOnly = (el) => {
                            steps.forEach(s => s?.classList.add(hiddenClass));
                            el?.classList.remove(hiddenClass);
                            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        };

                        showOnly(stepClient);
                    })
                    .catch(error => {
                        console.error('Save schedule failed', error);
                        //  show error message to user
                        showAlert('Failed to save the selected appointment time. Please try again.', 'error');
                    });
            } else {
                // show alert if date or timeSlot is missing
                showAlert('Please select a valid date and time for your appointment.', 'error');
            }
        };
        
        // Remove old event listeners if they exist
        if (r._changeHandler) {
         //   r.removeEventListener('change', r._changeHandler);
        }
        if (r._clickHandler) {
            r.removeEventListener('click', r._clickHandler);
        }
        if (label && label._labelClickHandler) {
            label.removeEventListener('click', label._labelClickHandler);
        }
        
        // Create new handlers and store references
       // r._changeHandler = () => { if (r.checked) goNext(); };
        r._clickHandler = () => { if (r.checked) goNext(); };
        
        // Add new event listeners
       // r.addEventListener('change', r._changeHandler);
        r.addEventListener('click', r._clickHandler);
        
        if (label) {
         //   label._labelClickHandler = () => { setTimeout(() => { if (r.checked) goNext(); }, 0); };
           // label.addEventListener('click', label._labelClickHandler);
        }
    });
}

/**
 * Save appointment schedule (date/time)
 */
export function saveSchedule({ date, time } = {}) {
    return request('/appointment/save-schedule', {
        method: 'POST',
        data: {
            date: date || '',
            time: time || ''
        }
    });
}

/**
 * Fetch appointment time slots for a specific date
 */
export function fetchTimeSlots(dateISO) {
    return new Promise((resolve, reject) => {
        if (!dateISO || !/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) {
            reject({ status: 400, statusText: 'Invalid date format', responseText: 'Date must be in YYYY-MM-DD format' });
            return;
        }

        const xhr = new XMLHttpRequest();
        const url = `/appointment/availability?date=${encodeURIComponent(dateISO)}`;
        
        xhr.open('GET', url, true);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        
        const csrf = getCsrf();
        xhr.setRequestHeader('X-CSRF-TOKEN', csrf.hash);
        
        xhr.onload = function() {
            setCsrfFromXhr(xhr);
            
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.responseText); // Return HTML directly
            } else {
                const error = {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    responseText: xhr.responseText,
                    responseJSON: null
                };
                try {
                    error.responseJSON = JSON.parse(xhr.responseText);
                } catch (e) {
                    // ignore
                }
                reject(error);
            }
        };
        
        xhr.onerror = function() {
            reject({ status: 0, statusText: 'Network Error', responseText: '' });
        };
        
        xhr.send();
    });
}

/**
 * Save client information
 */
export function saveClientInfo(formData = {}) {
     // if we do not have <meta name="csrf_token"> in the page, the request will fail, so we simulate  loading state for local testing 
                const csrfTokenMeta = document.querySelector('meta[name="csrf_token"]');
                if (!csrfTokenMeta) {
                    console.log('Simulating client info save (no CSRF token found). Form data:', formData);
                    return new Promise((resolve) => {
                        setTimeout(() => {
                            resolve({ success: true });
                        }, 1000);
                    });
                }
    return request('/appointment/client', {
        method: 'POST',
        data: formData
    });
}

/**
 * Show loading state on element
 */
export function showLoading(element, loadingClass = 'loading') {
    if (element) {
        // Save original text/content
        if (!element._originalContent) {
            element._originalContent = element.textContent || element.innerHTML;
        }
        
        element.disabled = true;
        element.classList.add(loadingClass);
        
        // Add loading text or spinner
        const loadingText = element.dataset.loadingText || 'Verifying...';
        if (element.textContent) {
            element.textContent = loadingText;
        }
        
        // Add visual loading indicator if needed
        element.style.opacity = '0.7';
        element.style.cursor = 'wait';
    }
}

/**
 * Hide loading state on element
 */
export function hideLoading(element, loadingClass = 'loading') {
    if (element) {
        element.disabled = false;
        element.classList.remove(loadingClass);
        
        // Restore original content
        if (element._originalContent) {
            if (element.textContent && !element.innerHTML.includes('<')) {
                element.textContent = element._originalContent;
            } else {
                element.innerHTML = element._originalContent;
            }
            delete element._originalContent;
        }
        
        // Remove visual loading indicators
        element.style.opacity = '';
        element.style.cursor = '';
    }
}

/**
 * Verify military ID through Idenfy service
 * @param {string} milID - Military ID to verify
 * @returns {Promise<Object>} Verification result with { success, valid, message, requiresFullVerification, verificationUrl, sessionToken, format }
 */
export function verifyMilID(milID) {
    if (!milID || typeof milID !== 'string') {
        return Promise.reject({
            success: false,
            valid: false,
            error: 'Military ID is required and must be a string',
            code: 'INVALID_INPUT'
        });
    }

    return request('/appointment/verify-mil-id', {
        method: 'POST',
        data: {
            mil_id: milID.trim()
        },
        onSuccess: (response) => {
            console.log('Military ID verification successful:', response);
        },
        onError: (xhr) => {
            console.error('Military ID verification request failed:', {
                status: xhr.status,
                statusText: xhr.statusText,
                responseText: xhr.responseText
            });
        }
    }).catch(error => {
        // Enhanced error handling
        console.error('Military ID verification error:', error);
        
        // If the error is already in the expected format, pass it through
        if (error && typeof error === 'object' && 'success' in error) {
            throw error;
        }
        
        // Transform network/parsing errors to consistent format
        const transformedError = {
            success: false,
            valid: false,
            error: 'Network error or server unavailable',
            code: 'NETWORK_ERROR'
        };
        
        if (error.status) {
            transformedError.httpStatus = error.status;
            
            // Try to extract meaningful error from response
            if (error.responseJSON) {
                transformedError.error = error.responseJSON.error || error.responseJSON.message || transformedError.error;
                transformedError.code = error.responseJSON.code || transformedError.code;
            } else if (error.responseText) {
                try {
                    const parsed = JSON.parse(error.responseText);
                    transformedError.error = parsed.error || parsed.message || transformedError.error;
                    transformedError.code = parsed.code || transformedError.code;
                } catch (parseError) {
                    // If we can't parse, use status text if available
                    if (error.statusText && error.statusText !== 'error') {
                        transformedError.error = error.statusText;
                    }
                }
            }
            
            // Set specific error messages based on status codes
            switch (error.status) {
                case 400:
                    transformedError.error = transformedError.error || 'Invalid request data';
                    break;
                case 422:
                    transformedError.error = transformedError.error || 'Invalid military ID format';
                    break;
                case 500:
                    transformedError.error = transformedError.error || 'Server error occurred';
                    break;
                case 503:
                    transformedError.error = 'Verification service temporarily unavailable';
                    break;
            }
        }
        
        throw transformedError;
    });
}

/**
 * Check military ID verification status
 * @param {string} sessionToken - Optional session token from initial verification
 * @returns {Promise<Object>} Status result with { success, valid, verified, message, status }
 */
export function checkMilIDVerification(sessionToken = null) {
    const data = sessionToken ? { session_token: sessionToken } : {};
    
    return request('/appointment/check-mil-id-verification', {
        method: 'POST',
        data: data,
        onSuccess: (response) => {
            console.log('Military ID verification status checked:', response);
        },
        onError: (xhr) => {
            console.error('Military ID verification status check failed:', {
                status: xhr.status,
                statusText: xhr.statusText,
                responseText: xhr.responseText
            });
        }
    }).catch(error => {
        console.error('Check verification status error:', error);
        
        // Transform error to consistent format
        const transformedError = {
            success: false,
            error: 'Failed to check verification status',
            code: 'STATUS_CHECK_ERROR'
        };
        
        if (error.status) {
            transformedError.httpStatus = error.status;
            
            if (error.responseJSON) {
                transformedError.error = error.responseJSON.error || transformedError.error;
                transformedError.code = error.responseJSON.code || transformedError.code;
            }
        }
        
        throw transformedError;
    });
}

/**
 * Open verification modal for document upload
 * @param {string} verificationUrl - Idenfy verification URL
 * @param {Object} options - Modal options { width, height }
 * @returns {Window} Modal window reference
 */
export function openVerificationModal(verificationUrl, options = {}) {
    const width = options.width || 800;
    const height = options.height || 600;
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;

    const modalWindow = window.open(
        verificationUrl,
        'Military ID Verification',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!modalWindow) {
        throw new Error('Popup blocked. Please allow popups for this site to complete verification.');
    }

    return modalWindow;
}

/**
 * Poll verification status until complete
 * @param {string} sessionToken - Idenfy session token
 * @param {Object} options - Polling options { interval, maxAttempts, onProgress }
 * @returns {Promise<Object>} Final verification status
 */
export function pollVerificationStatus(sessionToken, options = {}) {
    const {
        interval = 5000,        // 5 seconds
        maxAttempts = 20,       // ~100 seconds total
        onProgress = null       // Callback for progress updates
    } = options;

    return new Promise(async (resolve, reject) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            // Wait before checking (except first attempt)
            if (attempt > 1) {
                await new Promise(res => setTimeout(res, interval));
            }

            try {
                const result = await checkMilIDVerification(sessionToken);

                // Call progress callback if provided
                if (onProgress) {
                    onProgress(attempt, maxAttempts, result);
                }

                console.log(`Polling attempt ${attempt}/${maxAttempts}:`, result);

                // Check if verification is complete
                if (result.verified) {
                    // Success!
                    resolve({
                        success: true,
                        verified: true,
                        status: result.status,
                        message: result.message || 'Military ID verified successfully',
                        attempts: attempt
                    });
                    return;
                } else if (result.status === 'REJECTED' || result.status === 'FAILED') {
                    // Failed
                    reject({
                        success: false,
                        verified: false,
                        status: result.status,
                        message: result.message || 'Verification was rejected',
                        attempts: attempt
                    });
                    return;
                }

                // Continue polling (PENDING status)
            } catch (error) {
                console.error(`Polling attempt ${attempt} failed:`, error);
                
                // If it's the last attempt, reject
                if (attempt === maxAttempts) {
                    reject({
                        success: false,
                        verified: false,
                        status: 'TIMEOUT',
                        message: 'Verification timeout - please try again',
                        attempts: attempt
                    });
                    return;
                }
                
                // Otherwise continue polling
            }
        }

        // Timeout
        reject({
            success: false,
            verified: false,
            status: 'TIMEOUT',
            message: 'Verification timeout - please try again',
            attempts: maxAttempts
        });
    });
}

/**
 * Complete military ID verification flow
 * @param {string} milID - Military ID to verify
 * @param {Object} callbacks - Event callbacks { onLoading, onModalOpen, onPollingStart, onProgress, onVerified, onError }
 * @returns {Promise<boolean>} Success status
 */
export async function verifyMilIDComplete(milID, callbacks = {}) {
    const {
        onLoading = null,
        onModalOpen = null,
        onPollingStart = null,
        onProgress = null,
        onVerified = null,
        onError = null
    } = callbacks;

    try {
        // Show loading
        if (onLoading) onLoading(true);

        // Step 1: Initial verification
        console.log('Step 1: Verifying military ID format...');
        const verifyResult = await verifyMilID(milID);

        if (verifyResult.success && verifyResult.valid) {
            // Already verified (cached or fallback)
            console.log('Military ID verified immediately!');
            if (onLoading) onLoading(false);
            if (onVerified) onVerified(verifyResult);
            return true;
        }

        if (verifyResult.requiresFullVerification && verifyResult.verificationUrl) {
            // Step 2: Open verification modal
            console.log('Step 2: Opening verification modal...');
            if (onModalOpen) onModalOpen(verifyResult);
            
            let modal = null;
            try {
                modal = openVerificationModal(verifyResult.verificationUrl);
            } catch (error) {
                console.error('Failed to open modal:', error);
                if (onError) onError(error);
                if (onLoading) onLoading(false);
                throw error;
            }

            // Step 3: Poll for status
            console.log('Step 3: Waiting for document verification...');
            if (onPollingStart) onPollingStart();

            try {
                const statusResult = await pollVerificationStatus(
                    verifyResult.sessionToken,
                    {
                        interval: 5000,
                        maxAttempts: 20,
                        onProgress: onProgress
                    }
                );

                // Close modal
                if (modal && !modal.closed) {
                    modal.close();
                }

                if (onLoading) onLoading(false);

                if (statusResult.verified) {
                    console.log('Military ID verified successfully!');
                    if (onVerified) onVerified(statusResult);
                    return true;
                } else {
                    const error = new Error(statusResult.message || 'Verification failed');
                    if (onError) onError(error);
                    throw error;
                }
            } catch (error) {
                // Close modal on error
                if (modal && !modal.closed) {
                    modal.close();
                }
                throw error;
            }
        }

        // If we get here, something unexpected happened
        throw new Error('Unexpected verification result');

    } catch (error) {
        console.error('Complete verification error:', error);
        if (onLoading) onLoading(false);
        if (onError) onError(error);
        throw error;
    }
}

/**
 * Show alert/notification (using browser alert for now, can be replaced with SweetAlert2)
 */
export function showAlert(message, type = 'info') {
    // For now using simple alert, can be replaced with SweetAlert2 if available
    if (window.Swal) {
        window.Swal.fire({
            icon: type,
            title: type === 'error' ? 'Error' : type === 'success' ? 'Success' : 'Info',
            text: message
        });
    } else {
        alert(message);
    }
}


export function checkEmailExists(email) {
    return request('/appointment/check-email', {
        method: 'POST',
        data: {
            email: email.trim()
        }
    });
}   
